import { FC, useEffect, useState } from 'react';
import {
  Grid,
  Theme,
  Typography,
  Button,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  DialogActions,
  Card,
  CardContent,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Alert } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import LoadingButtonIndicator from 'components/LoadingButtonIndicator';
import axios, { CancelTokenSource } from 'axios';
import HeaderRow from 'components/HeaderRow';
import BodyRow from '../JobServiceItemContent/components/BodyRow';
import NumberFormat from 'react-number-format';
import ServiceItemModal from './components/ServiceItemModal';
import { dummyServiceItem } from 'constants/dummy';
import {
  GET_EDIT_JOB_SERVICE_ITEM_URL,
  GET_EQUIPEMENT_BY_SERVICE_ADDRESS_ID_URL,
  SERVICE_ITEM_TEMPLATE_BASE_URL,
  GET_USER_VERIFY_PASSWORD_URL
} from 'constants/url';
import CustomizedDialog from 'components/CustomizedDialog';
import theme from 'theme';
import NumberFormatCustom from 'components/NumberFormatCustom';

interface Props {
  job: JobDetailModel;
  serviceItems: ServiceItemModel[];
  additionalServiceItems: ServiceItemModel[];
  handleClose(): void;
  handleSnackbar: (variant: 'success' | 'error', message: string) => void;
  fetchData(): void;
}

const useStyles = makeStyles((theme: Theme) => ({
  contentGrid: {
    padding: theme.spacing(2),
    maxHeight: 580,
    overflow: 'auto'
  },
  card: {
    borderRadius: 10
  },
  noneBorder: {
    borderStyle: 'none'
  },
  radioButton: {
    padding: theme.spacing(1),
    margin: '8px 0',
    border: '1px solid',
    borderRadius: 10
  },
  discountField: {
    textAlign: 'right',
    width: 50,
    padding: 8
  }
}));

const FULLY_PAID = 'FULLY PAID';

interface InvoicePreview {
  newInvoiceAmount: number;
  newStatus: string;
  balance: number;
}

// Mirrors the backend rule (resolveInvoiceStatusAfterAmountChange) to preview the invoice after saving
const getInvoicePreview = (invoiceAmount: number, collectedAmount: number, currentStatus: string, amountChange: number): InvoicePreview => {
  const newInvoiceAmount = Number((invoiceAmount + amountChange).toFixed(2));
  const newStatus =
    currentStatus === 'VOID' ? currentStatus : collectedAmount >= newInvoiceAmount ? FULLY_PAID : collectedAmount > 0 ? 'PARTIALLY PAID' : 'UNPAID';

  return { newInvoiceAmount, newStatus, balance: Number((newInvoiceAmount - collectedAmount).toFixed(2)) };
};

const ServiceItemForm: FC<Props> = props => {
  const classes = useStyles();
  const { job, serviceItems, additionalServiceItems, handleClose, handleSnackbar, fetchData } = props;
  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const { serviceAddressId, needGST, gstTax, jobDiscountAmount, additionalServiceId, additionalGstTax } = job;
  const [serviceItemMaster, setServiceItemMaster] = useState<ServiceItemModel[]>([]);
  const [equipmentMaster, setEquipmentMaster] = useState<EquipmentModel[]>([]);

  const isHaveAdditional = additionalServiceId && additionalServiceId > 0 ? true : false;
  // only a FULLY PAID invoice that has received money is protected; a $0 one (e.g. a not chargeable job) is edited
  // like an unpaid one
  const isMainInvoicePaid = job.invoiceStatus === FULLY_PAID && Number(job.invoiceCollectedAmount || 0) > 0;
  const isAdditionalInvoicePaid =
    isHaveAdditional && job.additionalInvoiceStatus === FULLY_PAID && Number(job.additionalInvoiceCollectedAmount || 0) > 0;
  // a FULLY PAID quotation stays read-only unless the user may edit paid invoices; each one is locked on its own,
  // so the other quotation can still be changed
  const isMainLocked = isMainInvoicePaid && !job.canEditFullyPaidInvoice;
  const isAdditionalLocked = isAdditionalInvoicePaid && !job.canEditFullyPaidInvoice;
  const [currentServiceItems, setCurrentServiceItems] = useState<ServiceItemModel[]>([]);
  const [currentAdditonalServiceItems, setCurrentAdditonalServiceItems] = useState<ServiceItemModel[]>([]);
  const [jobAmount, setJobAmount] = useState<number>(0);
  const [gstAmount, setGstAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [additionalJobAmount, setAdditionalJobAmount] = useState<number>(0);
  const [additionalGst, setAdditionalGst] = useState<number>(0);
  const [additionalDiscountAmount, setAdditionalDiscountAmount] = useState<number>(0);
  const [additionalGstAmount, setAdditionalGstAmout] = useState<number>(0);
  const [additionalTotalAmount, setAdditionalTotalAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openItemModal, setOpenItemModal] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [type, setType] = useState<string>('original');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const [openSelectType, setOpenSelectType] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>(isMainLocked ? 'additional' : 'original');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<{ index: number; type: string } | null>(null);

  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState<boolean>(false);
  const [savePassword, setSavePassword] = useState<string>('');
  const [savePasswordError, setSavePasswordError] = useState<string>('');
  const [isShowSavePassword, setShowSavePassword] = useState<boolean>(false);
  const [saveReason, setSaveReason] = useState<string>('');
  const [isVerifyingSavePassword, setIsVerifyingSavePassword] = useState<boolean>(false);
  const [isSaveConfirmed, setIsSaveConfirmed] = useState<boolean>(false);
  const [saveReasonError, setSaveReasonError] = useState<string>('');
  // only send a quotation when it actually changed, so an untouched (e.g. paid) one is never resubmitted
  const [isMainChanged, setIsMainChanged] = useState<boolean>(false);
  const [isAdditionalChanged, setIsAdditionalChanged] = useState<boolean>(false);
  // a change that moves the amount (item added, removed, or its name, qty, price or total edited); a description-only
  // edit is not one, so a FULLY PAID invoice takes it without the password and reason
  const [isMainAmountChanged, setIsMainAmountChanged] = useState<boolean>(false);
  const [isAdditionalAmountChanged, setIsAdditionalAmountChanged] = useState<boolean>(false);

  // the save touches a FULLY PAID invoice only when that quotation changed
  const isPaidInvoiceAffected = (isMainInvoicePaid && isMainAmountChanged) || (isAdditionalInvoicePaid && isAdditionalAmountChanged);
  // on a locked (FULLY PAID) quotation only the description of an existing item can still be edited
  const isDescriptionOnlyEdit = isEdit && (type === 'original' ? isMainLocked : isAdditionalLocked);

  const isItemAmountChanged = (previous: ServiceItemModel | undefined, next: ServiceItemModel) =>
    !previous ||
    previous.name.trim() !== next.name.trim() ||
    Number(previous.quantity) !== Number(next.quantity) ||
    Number(previous.unitPrice) !== Number(next.unitPrice) ||
    Number(previous.discountAmt || 0) !== Number(next.discountAmt || 0) ||
    Number(previous.totalPrice) !== Number(next.totalPrice);

  useEffect(() => {
    if (job) {
      const {
        jobAmount,
        gstAmount,
        totalAmount,
        additionalJobAmount,
        additionalGstAmount,
        additionalDiscountAmount,
        additionalTotalAmount,
        defaultGst
      } = job;
      setJobAmount(jobAmount);
      setGstAmount(gstAmount);
      setTotalAmount(totalAmount);
      setAdditionalJobAmount(additionalJobAmount);
      // A new separate quotation follows the main quotation's GST setting: use the default GST rate
      // only when the main quotation needs GST, otherwise 0% (so the label matches a non-GST parent).
      setAdditionalGst(isHaveAdditional ? additionalGstTax : needGST ? defaultGst : 0);
      setAdditionalGstAmout(additionalGstAmount);
      setAdditionalDiscountAmount(additionalDiscountAmount);
      setAdditionalTotalAmount(additionalTotalAmount);
      setCurrentServiceItems(
        serviceItems.map(value => {
          return { ...value };
        })
      );

      if (additionalServiceItems) {
        setCurrentAdditonalServiceItems(
          additionalServiceItems.map(value => {
            return { ...value };
          })
        );
      }
    }

    const getServiceItemTemplate = async () => {
      //Set service item master
      const { data } = await axios.get(`${SERVICE_ITEM_TEMPLATE_BASE_URL}`, { cancelToken: cancelTokenSource.token });

      let serviceItemData: ServiceItemModel[] = [];
      data.serviceItemTemplates.map((value: any) => {
        return serviceItemData.push({
          ...value,
          totalPrice: 0,
          isDeleted: false
        });
      });
      setServiceItemMaster(serviceItemData);
    };

    const getEquipmentsTemplate = async () => {
      const { data } = await axios.get(`${GET_EQUIPEMENT_BY_SERVICE_ADDRESS_ID_URL(serviceAddressId)}`, {
        cancelToken: cancelTokenSource.token
      });

      let equipmentData: EquipmentModel[] = [];
      if (data.equipments.length > 0) {
        equipmentData = data.equipments;
      }
      equipmentData = equipmentData.sort((a, b) => a.brand.localeCompare(b.brand));

      setEquipmentMaster(equipmentData);
    };

    getServiceItemTemplate();
    getEquipmentsTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditAction = (index: number, type: string) => {
    setIsEdit(true);
    setOpenItemModal(true);
    setSelectedIndex(index);
    setType(type);
  };

  const handleDeleteAction = (index: number, type: string) => {
    setPendingDelete({ index, type });
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setPendingDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    const { index, type } = pendingDelete;

    if (type === 'original') {
      const serviceItems = [...currentServiceItems];
      const target = serviceItems[index];

      if (target.isNew) {
        serviceItems.splice(index, 1);
      } else {
        serviceItems[index] = { ...target, isDeleted: true };
      }

      const activeItems = serviceItems.filter(value => !value.isDeleted);
      const newJobAmount = activeItems.reduce((accumulator, value) => accumulator + value.totalPrice, 0);
      let newTotalAmount = Number((newJobAmount - jobDiscountAmount).toFixed(2));
      let newGstAmount = 0;

      if (needGST) {
        newGstAmount = Number(((newTotalAmount * gstTax) / 100).toFixed(2));
        newTotalAmount = Number((newTotalAmount + newGstAmount).toFixed(2));
      }

      setJobAmount(newJobAmount);
      setGstAmount(newGstAmount);
      setTotalAmount(newTotalAmount);
      setCurrentServiceItems(serviceItems);
      setIsMainChanged(true);
      setIsMainAmountChanged(true);
    } else {
      const additionalItems = [...currentAdditonalServiceItems];
      const target = additionalItems[index];

      if (target.isNew) {
        additionalItems.splice(index, 1);
      } else {
        additionalItems[index] = { ...target, isDeleted: true };
      }

      const activeItems = additionalItems.filter(value => !value.isDeleted);
      const newAdditionalJobAmount = activeItems.reduce((accumulator, value) => accumulator + value.totalPrice, 0);
      let newAdditionalTotalAmount = Number((newAdditionalJobAmount - additionalDiscountAmount).toFixed(2));
      let newAdditionalGstAmount = 0;

      if (needGST) {
        newAdditionalGstAmount = Number(((newAdditionalTotalAmount * additionalGst) / 100).toFixed(2));
        newAdditionalTotalAmount = Number((newAdditionalTotalAmount + newAdditionalGstAmount).toFixed(2));
      }

      setAdditionalJobAmount(newAdditionalJobAmount);
      setAdditionalGstAmout(newAdditionalGstAmount);
      setAdditionalTotalAmount(newAdditionalTotalAmount);
      setCurrentAdditonalServiceItems(additionalItems);
      setIsAdditionalChanged(true);
      setIsAdditionalAmountChanged(true);
    }

    setConfirmDeleteOpen(false);
    setPendingDelete(null);
  };

  const renderServiceItems = () => {
    return currentServiceItems.map((value, index) => {
      if (value.isDeleted) {
        return null;
      }
      return (
        <BodyRow
          key={index}
          index={index}
          item={value}
          actionButton
          isDeleteHidden={isMainLocked}
          handleEditAction={() => handleEditAction(index, 'original')}
          handleDeleteAction={() => handleDeleteAction(index, 'original')}
        />
      );
    });
  };

  const renderAdditonalServiceItems = () => {
    return currentAdditonalServiceItems.map((value, index) => {
      if (value.isDeleted) {
        return null;
      }
      return (
        <BodyRow
          key={index}
          index={index}
          item={value}
          actionButton
          isDeleteHidden={isAdditionalLocked}
          handleEditAction={() => handleEditAction(index, 'additional')}
          handleDeleteAction={() => handleDeleteAction(index, 'additional')}
        />
      );
    });
  };

  const renderFooter = (title: string, value: any) => {
    return (
      <>
        <Grid item container justify='flex-end' xs={11}>
          <Typography variant='body1'>{title}</Typography>
        </Grid>
        <Grid item container justify='flex-end' xs={1}>
          <Typography variant='body1'>
            <NumberFormat value={value || 0} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={2} fixedDecimalScale={true} />
          </Typography>
        </Grid>
      </>
    );
  };

  const handleSubmitModal = (item: ServiceItemModel) => {
    if (item.name) {
      if (type === 'original') {
        const currentItems = currentServiceItems;
        const isAmountChanged = isItemAmountChanged(isEdit ? currentItems[selectedIndex] : undefined, item);
        let gstAmount = 0;
        let totalAmount = 0;

        if (isEdit) {
          const findSameIndex = currentItems.findIndex(
            value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
          );

          if (findSameIndex === selectedIndex) {
            currentItems[selectedIndex] = item;
          } else {
            const findSame = currentItems.some(
              value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
            );
            if (!findSame) {
              currentItems[selectedIndex] = item;
            } else {
              handleSnackbar('error', 'This item is already added');
              return;
            }
          }
        } else {
          const findSame = currentItems.some(
            value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
          );

          if (!findSame) {
            const serviceId = currentItems[0].serviceId;
            const scheduleId = currentItems[0].scheduleId;
            currentItems.push({ ...item, isNew: true, serviceId, scheduleId });
          } else {
            handleSnackbar('error', 'This item is already added');
            return;
          }
        }
        const jobAmount = currentItems
          .filter(value => !value.isDeleted)
          .reduce((accumulator, value) => {
            return accumulator + value.totalPrice;
          }, 0);

        totalAmount = Number((jobAmount - jobDiscountAmount).toFixed(2));

        if (needGST) {
          gstAmount = Number(((totalAmount * gstTax) / 100).toFixed(2));
          totalAmount = Number((totalAmount + gstAmount).toFixed(2));
        }

        setJobAmount(jobAmount);
        setGstAmount(gstAmount);
        setTotalAmount(totalAmount);
        setCurrentServiceItems(currentItems);
        setIsMainChanged(true);
        if (isAmountChanged) {
          setIsMainAmountChanged(true);
        }
      } else {
        const currentItems = currentAdditonalServiceItems;
        const isAmountChanged = isItemAmountChanged(isEdit ? currentItems[selectedIndex] : undefined, item);
        let additionalGstAmount = 0;
        let additionalTotalAmount = 0;

        if (isEdit) {
          const findSameIndex = currentItems.findIndex(
            value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
          );
          if (findSameIndex === selectedIndex) {
            currentItems[selectedIndex] = item;
          } else {
            const findSame = currentItems.some(
              value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
            );
            if (!findSame) {
              currentItems[selectedIndex] = item;
            } else {
              handleSnackbar('error', 'This item is already added');
              return;
            }
          }
        } else {
          const findSame = currentItems.some(
            value => !value.isDeleted && value.name.trim() === item.name.trim() && value.description.trim() === item.description.trim()
          );
          if (!findSame) {
            currentItems.push({ ...item, isNew: true });
          } else {
            handleSnackbar('error', 'This item is already added');
            return;
          }
        }
        const additionalJobAmount = currentItems
          .filter(value => !value.isDeleted)
          .reduce((accumulator, value) => {
            return accumulator + value.totalPrice;
          }, 0);

        additionalTotalAmount = Number((additionalJobAmount - additionalDiscountAmount).toFixed(2));

        if (needGST) {
          additionalGstAmount = Number(((additionalTotalAmount * additionalGst) / 100).toFixed(2));
          additionalTotalAmount = Number((additionalTotalAmount + additionalGstAmount).toFixed(2));
        }

        setAdditionalJobAmount(additionalJobAmount);
        setAdditionalGstAmout(additionalGstAmount);
        setAdditionalTotalAmount(additionalTotalAmount);
        setCurrentAdditonalServiceItems(currentItems);
        setIsAdditionalChanged(true);
        if (isAmountChanged) {
          setIsAdditionalAmountChanged(true);
        }
      }
      setOpenItemModal(false);
    } else {
      handleSnackbar('error', 'Please input item');
      return;
    }
  };

  const handleChangeAdditionalDiscount = (value: number) => {
    let totalDiscountAmount = additionalDiscountAmount;
    let totalGstAmount = additionalGstAmount;
    let grandTotal = additionalTotalAmount;

    totalDiscountAmount = value > additionalJobAmount ? additionalJobAmount : value;
    grandTotal = Number((additionalJobAmount - totalDiscountAmount).toFixed(2));

    if (needGST) {
      totalGstAmount = Number(((grandTotal * additionalGst) / 100).toFixed(2));
      grandTotal = grandTotal + totalGstAmount;
    }
    setAdditionalDiscountAmount(Number(totalDiscountAmount.toFixed(2)));
    setAdditionalGstAmout(totalGstAmount);
    setAdditionalTotalAmount(grandTotal);
    setIsAdditionalChanged(true);
    setIsAdditionalAmountChanged(true);
  };

  const performSubmit = async (reason?: string, password?: string) => {
    const applyReason = (items: ServiceItemModel[]) => items.map(item => (item.isDeleted ? { ...item, deleteReason: reason } : item));

    const serviceItemsPayload = applyReason(currentServiceItems);
    const additionalServiceItemsPayload = applyReason(currentAdditonalServiceItems);
    const activeAdditionalItems = additionalServiceItemsPayload.filter(value => !value.isDeleted);
    // If every additional item got deleted, remove the whole separate quotation (and its invoice)
    // instead of updating it with an empty item list
    const isDeletingAllAdditionalItems = isHaveAdditional && activeAdditionalItems.length === 0;

    // Main and separate quotation are saved in one request (one transaction): either both are saved or neither
    let AdditionalService: { action: string; ServiceItems?: ServiceItemModel[]; discountAmount?: number } | undefined;
    if (isDeletingAllAdditionalItems) {
      AdditionalService = { action: 'DELETE' };
    } else if (!isHaveAdditional && additionalServiceItemsPayload.length > 0) {
      AdditionalService = { action: 'CREATE', ServiceItems: additionalServiceItemsPayload, discountAmount: additionalDiscountAmount };
    } else if (isHaveAdditional && isAdditionalChanged) {
      AdditionalService = { action: 'UPDATE', ServiceItems: additionalServiceItemsPayload, discountAmount: additionalDiscountAmount };
    }

    // nothing changed: there is nothing to save
    if (!isMainChanged && !AdditionalService) {
      handleClose();
      return;
    }

    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    setIsLoading(true);

    try {
      await axios.put(
        `${GET_EDIT_JOB_SERVICE_ITEM_URL(job.jobId)}`,
        { ServiceItems: isMainChanged ? serviceItemsPayload : undefined, AdditionalService, reason, password },
        { cancelToken: cancelTokenSource.token }
      );

      fetchData();
      setIsLoading(false);
      handleSnackbar('success', 'Successfully edit service item');
      handleClose();
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      handleSnackbar('error', 'Failed edit service item');
    }
  };

  const resetSaveConfirmationState = () => {
    setSaveConfirmationOpen(false);
    setSavePassword('');
    setSavePasswordError('');
    setSaveReason('');
    setShowSavePassword(false);
    setIsSaveConfirmed(false);
    setSaveReasonError('');
  };

  const handleCancelSaveConfirmation = () => {
    resetSaveConfirmationState();
  };

  const handleConfirmSaveWithPassword = async () => {
    if (!isSaveConfirmed) {
      handleSnackbar('error', 'Please tick to confirm the action before proceeding.');
      return;
    }

    if (!savePassword) {
      setSavePasswordError('Please input current password');
      return;
    }

    if (isPaidInvoiceAffected && !saveReason.trim()) {
      setSaveReasonError('Please input the reason for changing a fully paid invoice');
      return;
    }

    setIsVerifyingSavePassword(true);
    try {
      const verifyCancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      const { data } = await axios.post(GET_USER_VERIFY_PASSWORD_URL, { password: savePassword }, { cancelToken: verifyCancelTokenSource.token });

      if (data) {
        const reason = saveReason;
        const password = savePassword;
        resetSaveConfirmationState();
        await performSubmit(reason, password);
      } else {
        setSavePasswordError('Invalid credentials');
      }
    } catch (err) {
      console.log(err);
      setSavePasswordError('Invalid credentials');
    }
    setIsVerifyingSavePassword(false);
  };

  const handleSaveClick = () => {
    const activeServiceItems = currentServiceItems.filter(value => !value.isDeleted);
    if (activeServiceItems.length === 0) {
      handleSnackbar('error', 'At least one service item is required in Current Quotation');
      return;
    }

    const isDeletingAllAdditionalItems = isHaveAdditional && currentAdditonalServiceItems.every(value => value.isDeleted);
    if (isAdditionalInvoicePaid && isDeletingAllAdditionalItems) {
      handleSnackbar('error', 'Cannot remove every item of a separate quotation whose invoice is fully paid');
      return;
    }

    const hasDeletedItems = currentServiceItems.some(value => value.isDeleted) || currentAdditonalServiceItems.some(value => value.isDeleted);

    // deleting items, or changing a fully paid invoice, needs the user's password and a reason
    if (hasDeletedItems || isPaidInvoiceAffected) {
      setSaveConfirmationOpen(true);
    } else {
      performSubmit();
    }
  };

  const formatAmount = (value: number) => `$${Math.abs(value).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderPaidInvoiceAlert = (
    label: string,
    invoiceAmount: number | null | undefined,
    collectedAmount: number | null | undefined,
    originalTotal: number,
    newTotal: number
  ) => {
    const collected = Number(collectedAmount || 0);
    const amountChange = Number((newTotal - originalTotal).toFixed(2));
    const preview = getInvoicePreview(Number(invoiceAmount || 0), collected, FULLY_PAID, amountChange);

    return (
      <Grid item xs={12}>
        <Alert severity='warning'>
          <Typography variant='body2'>
            The invoice of the {label} is already <b>FULLY PAID</b> ({formatAmount(collected)} collected). Saving will adjust the invoice amount and
            its status when its service items change, and requires your password and a reason.
          </Typography>
          {amountChange !== 0 && (
            <Typography variant='body2' style={{ marginTop: 4 }}>
              Estimated new invoice amount: <b>{formatAmount(preview.newInvoiceAmount)}</b> &rarr; status <b>{preview.newStatus}</b>
              {preview.balance > 0 && <>, outstanding {formatAmount(preview.balance)}</>}
              {preview.balance < 0 && <>, over-payment {formatAmount(preview.balance)} (refund/credit to be handled manually)</>}
            </Typography>
          )}
        </Alert>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2} className={classes.contentGrid} alignItems='center'>
        {isMainInvoicePaid &&
          (isMainLocked ? (
            <Grid item xs={12}>
              <Alert severity='info'>
                The current quotation's invoice is fully paid, so only the description of its service items can be changed.
              </Alert>
            </Grid>
          ) : (
            renderPaidInvoiceAlert('current quotation', job.invoiceAmount, job.invoiceCollectedAmount, job.totalAmount, totalAmount)
          ))}
        {isAdditionalInvoicePaid &&
          (isAdditionalLocked ? (
            <Grid item xs={12}>
              <Alert severity='info'>
                The separate quotation's invoice is fully paid, so only the description of its service items can be changed.
              </Alert>
            </Grid>
          ) : (
            renderPaidInvoiceAlert(
              'separate quotation',
              job.additionalInvoiceAmount,
              job.additionalInvoiceCollectedAmount,
              job.additionalTotalAmount,
              additionalTotalAmount
            )
          ))}
        <Grid item xs={12} sm={10}>
          <Typography variant='h4'>All Service Items</Typography>
        </Grid>
        <Grid item container justify='flex-end' xs={12} sm={2}>
          <Button
            color='primary'
            disabled={isMainLocked && isAdditionalLocked}
            onClick={() => {
              setSelectedType(isMainLocked ? 'additional' : 'original');
              setOpenSelectType(true);
              setIsEdit(false);
            }}
          >
            <AddIcon />
            Add Service Items
          </Button>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 16 }}>
          <Typography variant='h5'>Current Quotation</Typography>
          <Typography variant='body1' color='textSecondary'>
            You can create new service items that only affected on this job under the contract
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card variant='outlined' className={classes.card}>
            <CardContent style={{ padding: 0 }}>
              <Table component='table'>
                <TableHead>
                  <HeaderRow
                    headers={[
                      { label: '', verticalAlign: 'top' },
                      { label: 'Item & Description', verticalAlign: 'top' },
                      { label: 'Qty', verticalAlign: 'top' },
                      { label: 'Price', verticalAlign: 'top' },
                      { label: 'Amount', verticalAlign: 'top', textAlign: 'center' },
                      { label: 'Action', verticalAlign: 'top', textAlign: 'center' }
                    ]}
                  />
                </TableHead>
                <TableBody>
                  {renderServiceItems()}
                  <TableRow>
                    <TableCell colSpan={5} className={classes.noneBorder}>
                      <Grid container spacing={1}>
                        {renderFooter('Job Amount', jobAmount)}
                        {renderFooter('Discount', jobDiscountAmount)}
                        {renderFooter(`GST ${gstTax}%`, gstAmount)}
                        {renderFooter('Total Amount', totalAmount)}
                      </Grid>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        {currentAdditonalServiceItems && currentAdditonalServiceItems.length > 0 && (
          <>
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant='h5'>Separate Quotation</Typography>
              <Typography variant='body1' color='textSecondary'>
                When you add new additional items here, it will create a new ad-hoc quotation and new invoice
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card variant='outlined' className={classes.card}>
                <CardContent style={{ padding: 0 }}>
                  <Table component='table'>
                    <TableHead>
                      <HeaderRow
                        headers={[
                          { label: '', verticalAlign: 'top' },
                          { label: 'Item & Description', verticalAlign: 'top' },
                          { label: 'Qty', verticalAlign: 'top' },
                          { label: 'Price', verticalAlign: 'top' },
                          { label: 'Amount', verticalAlign: 'top', textAlign: 'center' },
                          { label: 'Action', verticalAlign: 'top', textAlign: 'center' }
                        ]}
                      />
                    </TableHead>
                    <TableBody>
                      <>
                        {renderAdditonalServiceItems()}
                        <TableRow>
                          <TableCell colSpan={5} className={classes.noneBorder}>
                            <Grid container spacing={1}>
                              {renderFooter('Additonal Amount', additionalJobAmount)}
                              <Grid item container justify='flex-end' alignItems='center' xs={11}>
                                <Typography variant='body1'>Discount</Typography>
                              </Grid>
                              <Grid item container justify='flex-end' xs={1}>
                                <TextField
                                  variant='outlined'
                                  margin='dense'
                                  id='discountAmount'
                                  disabled={isAdditionalLocked}
                                  value={additionalDiscountAmount}
                                  onChange={event => handleChangeAdditionalDiscount(Number(event.target.value))}
                                  InputProps={{
                                    classes: {
                                      input: classes.discountField
                                    },
                                    inputComponent: NumberFormatCustom as any,
                                    inputProps: {
                                      prefix: '$',
                                      thousandSeparator: true,
                                      decimalScale: 2,
                                      fixedDecimalScale: true,
                                      allowNegative: false
                                    }
                                  }}
                                />
                              </Grid>
                              {renderFooter(`GST ${additionalGst}%`, additionalGstAmount)}
                              {renderFooter('Total Additional Amount', additionalTotalAmount)}
                            </Grid>
                          </TableCell>
                        </TableRow>
                      </>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
        {openSelectType && (
          <CustomizedDialog
            isLoading={isLoading}
            open={openSelectType}
            isConfirmation={true}
            variant='warning'
            title='Add New Service Items'
            message=''
            secondMessage='Please specify the addition of service items you did just now'
            primaryButtonLabel='Confirm'
            secondaryButtonLabel='Cancel'
            primaryActionButton={() => {
              setType(selectedType);
              setOpenItemModal(true);
              setOpenSelectType(false);
            }}
            secondaryActionButton={() => setOpenSelectType(false)}
            handleClose={() => setOpenSelectType(false)}
          >
            <FormControl component='fieldset' style={{ margin: '16px 0' }}>
              <RadioGroup aria-label='contractType' name='contractType' value={selectedType} onChange={event => setSelectedType(event.target.value)}>
                <FormControlLabel
                  value={'original'}
                  disabled={isMainLocked}
                  control={<Radio color='primary' />}
                  label={
                    <>
                      <Typography variant='subtitle1'>Current Quotation</Typography>
                      <FormHelperText>You can add new service items that only affected on this job under the quotation</FormHelperText>
                    </>
                  }
                  className={classes.radioButton}
                  style={{ borderColor: selectedType === 'original' ? theme.palette.primary.main : theme.palette.grey[500] }}
                />
                <FormControlLabel
                  value={'additional'}
                  disabled={isAdditionalLocked}
                  control={<Radio color='primary' />}
                  label={
                    <>
                      <Typography variant='subtitle1'>Separate Quotation</Typography>
                      <FormHelperText>You can add new service items that affecting to create a new separate quotation and new invoice</FormHelperText>
                    </>
                  }
                  className={classes.radioButton}
                  style={{ borderColor: selectedType === 'additional' ? theme.palette.primary.main : theme.palette.grey[500] }}
                />
              </RadioGroup>
            </FormControl>
          </CustomizedDialog>
        )}
        {confirmDeleteOpen && (
          <CustomizedDialog
            isLoading={false}
            open={confirmDeleteOpen}
            isConfirmation={true}
            variant='warning'
            message='Are you sure you want to delete this service item?'
            secondMessage='This will only take effect after you Save.'
            primaryButtonLabel='Yes'
            secondaryButtonLabel='No'
            primaryActionButton={handleConfirmDelete}
            secondaryActionButton={handleCancelDelete}
            handleClose={handleCancelDelete}
          />
        )}
        {saveConfirmationOpen && (
          <CustomizedDialog
            isLoading={isVerifyingSavePassword}
            open={saveConfirmationOpen}
            isConfirmation={true}
            variant='warning'
            title={isPaidInvoiceAffected ? 'Confirm Change to Fully Paid Invoice' : 'Confirm Deletion'}
            message={
              isPaidInvoiceAffected
                ? 'This change adjusts the amount of a fully paid invoice. Please enter your current password and a reason to proceed.'
                : 'One or more service items will be deleted. Please enter your current password and a reason to proceed.'
            }
            primaryButtonLabel='Confirm'
            secondaryButtonLabel='Cancel'
            primaryActionButton={handleConfirmSaveWithPassword}
            secondaryActionButton={handleCancelSaveConfirmation}
            handleClose={handleCancelSaveConfirmation}
          >
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin='dense'
                  variant='outlined'
                  autoComplete='off'
                  id='saveServiceItemPassword'
                  label='Current Password'
                  type={isShowSavePassword ? 'text' : 'password'}
                  error={savePasswordError !== ''}
                  helperText={savePasswordError}
                  value={savePassword}
                  onChange={event => {
                    setSavePassword(event.target.value);
                    setSavePasswordError('');
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton edge='end' aria-label='toggle password visibility' onClick={() => setShowSavePassword(!isShowSavePassword)}>
                          {isShowSavePassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin='dense'
                  variant='outlined'
                  id='saveServiceItemReason'
                  label='Reason'
                  required={isPaidInvoiceAffected}
                  error={saveReasonError !== ''}
                  helperText={saveReasonError}
                  multiline
                  rows={3}
                  value={saveReason}
                  onChange={event => {
                    setSaveReason(event.target.value);
                    setSaveReasonError('');
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={isSaveConfirmed} onChange={event => setIsSaveConfirmed(event.target.checked)} color='primary' />}
                  label='I confirm that I want to proceed with this action.'
                />
              </Grid>
            </Grid>
          </CustomizedDialog>
        )}
        {openItemModal && (
          <ServiceItemModal
            open={openItemModal}
            serviceItemMaster={serviceItemMaster}
            equipmentMaster={equipmentMaster}
            serviceItem={
              isEdit ? (type === 'original' ? currentServiceItems[selectedIndex] : currentAdditonalServiceItems[selectedIndex]) : dummyServiceItem
            }
            isEdit={isEdit}
            isDescriptionOnly={isDescriptionOnlyEdit}
            handleSubmitModal={handleSubmitModal}
            handleClose={() => setOpenItemModal(false)}
          />
        )}
      </Grid>
      <DialogActions>
        <Button variant='contained' disableElevation disabled={isLoading} style={{ marginTop: 8 }} onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' disableElevation disabled={isLoading} style={{ marginTop: 8 }} onClick={handleSaveClick}>
          Save
          <LoadingButtonIndicator isLoading={isLoading} />
        </Button>
      </DialogActions>
    </>
  );
};

export default ServiceItemForm;
